import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "./AddBreakdown.css";

const AddUserForm = ({ isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    password: "",
    userrole: "user",
    badge: "",
    fleetNo: "",
    signature: null,
    signatureType: "upload",
  });
  const [message, setMessage] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [isUserSaved, setIsUserSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFetchedDetails, setHasFetchedDetails] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [lineWidth, setLineWidth] = useState(2);
  const [lineColor, setLineColor] = useState("#000000");
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id && !hasFetchedDetails) {
      fetchUserDetails(id);
      setHasFetchedDetails(true);
    }
    setupCanvas();
  }, [id, hasFetchedDetails, lineWidth, lineColor]);

  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => {
        setMessage("");
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [message]);

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = lineColor;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const payload = Array.isArray(response.data) ? response.data[0] || {} : response.data || {};

      const normalized = {
        name: payload.name || payload.NAME || "",
        username: payload.username || payload.USERNAME || "",
        email: payload.email || payload.EMAIL || "",
        // Fill password from API if provided (requested)
        password: payload.password || "",
        userrole: payload.userrole || payload.USERROLE || payload.user_role || "user",
        badge: payload.badge && payload.badge !== "NULL" ? payload.badge : "",
        fleetNo: payload.fleetno && payload.fleetno !== "NULL" ? payload.fleetno : (payload.fleetNo || ""),
        AWS_URL: payload.aws_url || payload.AWS_URL || payload.awsUrl || null,
        userId: payload.user_id || payload.USER_ID || userId,
      };

      setFormData((prev) => ({ ...prev, ...normalized }));
      setIsUserSaved(true);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, signature: e.target.files[0] }));
  };

  const handleDeleteSignature = async () => {
    if (window.confirm("Are you sure you want to delete this signature?")) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(
          `${process.env.REACT_APP_BASE_API_URL}/delete-signature/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFormData((prev) => ({ ...prev, AWS_URL: null }));
        alert("Signature deleted successfully!");
      } catch (error) {
        console.error("Error deleting signature:", error);
        alert("Failed to delete signature.");
      }
    }
  };

  const handleSignatureTypeChange = (type) => {
    setFormData((prev) => ({ ...prev, signatureType: type, signature: null }));
    if (type === "upload") {
      setShowUploadOptions(true);
    } else {
      setShowUploadOptions(false);
    }
  };

  const getCanvasCoordinates = (canvas, clientX, clientY) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const { x, y } = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(canvas, touch.clientX, touch.clientY);
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const { x, y } = getCanvasCoordinates(canvas, e.clientX, e.clientY);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handleTouchMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const touch = e.touches[0];
    const { x, y } = getCanvasCoordinates(canvas, touch.clientX, touch.clientY);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.closePath();
    setIsDrawing(false);
  };

  const handleTouchEnd = () => {
    endDrawing();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const dataURLToBlob = (dataURL) => {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const saveCanvasImage = async (userId) => {
    const canvas = canvasRef.current;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width * 2;
    tempCanvas.height = canvas.height * 2;
    const tempCtx = tempCanvas.getContext("2d");
    tempCtx.scale(2, 2);
    tempCtx.drawImage(canvas, 0, 0);

    const dataURL = tempCanvas.toDataURL("image/png");
    const blob = dataURLToBlob(dataURL);
    const file = new File([blob], "signature.png", { type: "image/png" });

    try {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("signature", file);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/upload-signature/${userId}`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        AWS_URL: response.data.data,
      }));

      alert("Signature saved and uploaded successfully!");
      setTimeout(() => {
        navigate("/add-user");
      }, 4000);
    } catch (error) {
      console.error("Error uploading signature:", error);
      alert("Failed to upload signature.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      let userId;

      if (isEditing) {
        await axios.patch(
          `${process.env.REACT_APP_BASE_API_URL}/users/${id}`,
          {
            name: formData.name,
            username: formData.username,
            email: formData.email,
            userrole: formData.userrole,
            badge: formData.badge || null,
            fleetNo: formData.fleetNo || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMessage("User updated successfully!");
        userId = id;
        setIsUserSaved(true);
        navigate("/add-user");
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/add-user`,
          {
            name: formData.name,
            username: formData.username,
            email: formData.email,
            password: formData.password,
            userrole: formData.userrole,
            badge: formData.badge || null,
            fleetNo: formData.fleetNo || null,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMessage("User added successfully!");
        userId = response.data.userId;
        setIsUserSaved(true);
      }

      setFormData((prev) => ({ ...prev, userId }));
    } catch (error) {
      console.error("Error saving user:", error);
      setMessage("Failed to save user.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadSignature = async (userId) => {
    if (!formData.signature) {
      alert("Please choose a file before uploading.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const uploadFormData = new FormData();
      uploadFormData.append("signature", formData.signature);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/upload-signature/${userId}`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        AWS_URL: uploadResponse.data.data,
      }));

      alert("Signature uploaded successfully!");
      setTimeout(() => {
        navigate("/add-user");
      }, 4000);
    } catch (error) {
      console.error("Error uploading signature:", error);
      alert("Failed to upload signature.");
    }
  };

  return (
    <div className="container-add-breakdown">
      <button
        className="close-button"
        onClick={() => navigate("/add-user")}
        aria-label="Close"
      >
        &times;
      </button>
      <form onSubmit={handleSubmit} className="form-grid-breakdown">
        {/* Existing form fields remain the same */}
        <div>
          <label className="form-label-breakdown">
            Name<span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown1"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Username<span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input-breakdown1"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            className="form-input-breakdown1"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Password <span className="required">*</span>
          </label>
          <input
            type="password"
            className="form-input-breakdown1"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required={!isEditing}
          />
        </div>
        <div>
          <label className="form-label-breakdown">
            Role <span className="required">*</span>
          </label>
          <select
            className="form-input-breakdown1 form-select-breakdown1"
            name="userrole"
            value={formData.userrole}
            onChange={handleChange}
            required
          >
            <option value="admin">Admin</option>
            <option value="technician">Technician</option>
            <option value="jm">JM</option>
            <option value="coo">COO</option>
            <option value="driver">Driver</option>
            <option value="oc">OC</option>
          </select>
        </div>

        {/* Conditionally show Fleet No and Badge fields if userrole is "driver" */}
        {formData.userrole === "driver" && (
          <>
            <div>
              <label className="form-label-breakdown">Badge</label>
              <input
                type="text"
                className="form-input-breakdown1"
                name="badge"
                value={formData.badge}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="form-label-breakdown">Fleet No</label>
              <input
                type="text"
                className="form-input-breakdown1"
                name="fleetNo"
                value={formData.fleetNo}
                onChange={handleChange}
              />
            </div>
          </>
        )}
        <div>
          <label className="form-label-breakdown">Signature</label>
          {!isUserSaved && (
            <p className="note">
              Please save the user information first. The signature section will
              be enabled afterward.
            </p>
          )}
          <div
            className="signature-box"
            style={{
              pointerEvents: isUserSaved ? "auto" : "none",
              opacity: isUserSaved ? 1 : 0.5,
            }}
          >
            <div className="signature-type-toggle">
              <button
                type="button"
                className={`toggle-button ${
                  formData.signatureType === "upload" ? "active" : ""
                }`}
                onClick={() => handleSignatureTypeChange("upload")}
                disabled={!!formData.AWS_URL || !isUserSaved}
              >
                Upload Signature
              </button>
              <button
                type="button"
                className={`toggle-button ${
                  formData.signatureType === "draw" ? "active" : ""
                }`}
                onClick={() => handleSignatureTypeChange("draw")}
                disabled={!!formData.AWS_URL || !isUserSaved}
              >
                Draw Signature
              </button>
            </div>

            {formData.AWS_URL ? (
              <div className="signature-preview">
                <img
                  src={`${formData.AWS_URL}?t=${new Date().getTime()}`}
                  alt="Signature"
                  className="signature-image"
                />
                <button
                  type="button"
                  className="delete-signature-button"
                  onClick={handleDeleteSignature}
                >
                  Delete Signature
                </button>
              </div>
            ) : formData.signatureType === "upload" && isUserSaved ? (
              <div className="signature-upload">
                <label
                  htmlFor="custom-file-upload"
                  className="custom-file-upload"
                >
                  Choose File
                </label>
                <input
                  id="custom-file-upload"
                  type="file"
                  accept=".png,.jpg"
                  onChange={handleFileChange}
                />
                {formData.signature && (
                  <p className="file-name">{formData.signature.name}</p>
                )}
                <button
                  type="button"
                  className="upload-signature-button"
                  onClick={() => handleUploadSignature(formData.userId || id)}
                  disabled={!isUserSaved}
                >
                  Upload Signature
                </button>
              </div>
            ) : formData.signatureType === "draw" && isUserSaved ? (
              <div className="signature-draw">
                <div className="signature-controls">
                  <label>Line Width:</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={lineWidth}
                    onChange={(e) => setLineWidth(e.target.value)}
                  />
                  <label>Color:</label>
                  <input
                    type="color"
                    value={lineColor}
                    onChange={(e) => setLineColor(e.target.value)}
                  />
                </div>
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={200}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={endDrawing}
                  onMouseLeave={endDrawing}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    border: "1px solid black",
                    touchAction: "none",
                  }}
                />
                <div className="signature-draw-buttons">
                  <button
                    type="button"
                    className="clear-canvas-button"
                    onClick={clearCanvas}
                  >
                    Clear Canvas
                  </button>
                  <button
                    type="button"
                    className="save-canvas-button"
                    onClick={() => saveCanvasImage(formData.userId || id)}
                  >
                    Save Signature
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <button
          type="submit"
          className="submit-button-breakdown"
          disabled={isSubmitting || (!isEditing && isUserSaved)}
        >
          {isEditing ? "Update" : "Submit"}
        </button>
        <button
          type="button"
          className="cancel-button-breakdown"
          onClick={() => navigate("/add-user")}
        >
          Cancel
        </button>
        {message && (
          <div
            className={`form-message ${
              message.toLowerCase().includes("success") ? "success" : "error"
            }`}
          >
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

export default AddUserForm;
